/**
 * Generated On: 2015-10-5
 * Class: WMTSProvider
 * Description: Fournisseur de données à travers un flux WMTS
 */

import * as THREE from 'three';
import OGCWebServiceHelper, { SIZE_TEXTURE_TILE } from './OGCWebServiceHelper';
import URLBuilder from './URLBuilder';
import Extent from '../Core/Geographic/Extent';
import { computeMinMaxElevation } from '../Parser/XbilParser';

const coordTile = new Extent('WMTS:WGS84', 0, 0, 0);

const supportedFormats = new Map([
    ['image/png', getColorTextures],
    ['image/jpg', getColorTextures],
    ['image/jpeg', getColorTextures],
    ['image/x-bil;bits=32', getXbilTexture],
]);

function preprocessDataLayer(layer) {
    layer.fx = layer.fx || 0.0;

    layer.format = layer.format || 'image/png';
    if (!supportedFormats.has(layer.format)) {
        throw new Error(
            `Layer ${layer.name}: unsupported layer.format '${layer.format}', must be one of '${Array.from(supportedFormats.keys()).join('\', \'')}'`);
    }

    if (layer.protocol === 'wmts') {
        const options = layer.options;
        options.version = options.version || '1.0.0';
        options.tileMatrixSet = options.tileMatrixSet || 'WGS84';
        options.style = options.style || 'normal';
        options.projection = options.projection || 'EPSG:3857';
        let newBaseUrl = `${layer.url}` +
            `?LAYER=${options.name}` +
            `&FORMAT=${layer.format}` +
            '&SERVICE=WMTS' +
            `&VERSION=${options.version}` +
            '&REQUEST=GetTile' +
            `&STYLE=${options.style}` +
            `&TILEMATRIXSET=${options.tileMatrixSet}`;

        newBaseUrl += '&TILEMATRIX=%TILEMATRIX&TILEROW=%ROW&TILECOL=%COL';

        if (!layer.options.zoom) {
            const arrayLimits = Object.keys(options.tileMatrixSetLimits);
            const size = arrayLimits.length;
            const maxZoom = Number(arrayLimits[size - 1]);
            const minZoom = maxZoom - size + 1;

            layer.options.zoom = {
                min: minZoom,
                max: maxZoom,
            };
        }
        layer.url = newBaseUrl;
    }
    layer.options.zoom = layer.options.zoom || { min: 2, max: 20 };
}

/**
 * return texture float alpha THREE.js of MNT
 * @param {TileMesh} tile
 * @param {Layer} layer
 * @param {number} targetZoom
 * @returns {Promise<portableXBIL>}
 */
function getXbilTexture(tile, layer, targetZoom) {
    const pitch = new THREE.Vector4(0.0, 0.0, 1.0, 1.0);
    let coordWMTS = tile.getCoordsForLayer(layer)[0];

    if (targetZoom && targetZoom !== coordWMTS.zoom) {
        coordWMTS = OGCWebServiceHelper.WMTS_WGS84Parent(coordWMTS, targetZoom, pitch);
    }

    const urld = URLBuilder.xyz(coordWMTS, layer);

    return OGCWebServiceHelper.getXBilTextureByUrl(urld, layer.networkOptions).then((texture) => {
        texture.coords = coordWMTS;
        let min;
        let max;

        // if pitch is full texture, the min and max are already known
        if (pitch.z == 1.0 && pitch.w == 1.0) {
            min = texture.min;
            max = texture.max;
        } else {
            const r = computeMinMaxElevation(texture.image.data,
                SIZE_TEXTURE_TILE, SIZE_TEXTURE_TILE,
                pitch);
            min = r.min;
            max = r.max;
        }

        return {
            min,
            max,
            texture,
            pitch,
        };
    });
}

/**
 * Return texture RGBA THREE.js of orthophoto
 * TODO : RGBA --> RGB remove alpha canal
 * @param {{zoom:number,row:number,col:number}} coordWMTS
 * @param {Layer} layer
 * @param {number} targetZoom
 * @returns {Promise<Texture>}
 */
function getColorTexture(coordWMTS, layer, targetZoom) {
    const pitch = new THREE.Vector4(0.0, 0.0, 1.0, 1.0);
    if (targetZoom && targetZoom !== coordWMTS.zoom) {
        coordWMTS = OGCWebServiceHelper.WMTS_WGS84Parent(coordWMTS, targetZoom, pitch);
    }

    const urld = URLBuilder.xyz(coordWMTS, layer);
    return OGCWebServiceHelper.getColorTextureByUrl(urld, layer.networkOptions).then((texture) => {
        const result = {};
        result.texture = texture;
        result.texture.coords = coordWMTS;
        result.pitch = pitch;
        if (layer.transparent) {
            texture.premultiplyAlpha = true;
        }

        return result;
    });
}

function executeCommand(command) {
    const layer = command.layer;
    const tile = command.requester;
    return supportedFormats.get(layer.format)(tile, layer, command.targetLevel);
}

function tileTextureCount(tile, layer) {
    const tileMatrixSet = layer.options.tileMatrixSet;
    OGCWebServiceHelper.computeTileMatrixSetCoordinates(tile, tileMatrixSet);
    return tile.getCoordsForLayer(layer).length;
}

function tileInsideLimit(tile, layer, targetLevel) {
    // This layer provides data starting at level = layer.options.zoom.min
    // (the zoom.max property is used when building the url to make
    //  sure we don't use invalid levels)
    for (const coord of tile.getCoordsForLayer(layer)) {
        let c = coord;
        // override
        if (targetLevel < c.zoom) {
            OGCWebServiceHelper.WMTS_WGS84Parent(coord, targetLevel, undefined, coordTile);
            c = coordTile;
        }
        if (c.zoom < layer.options.zoom.min || c.zoom > layer.options.zoom.max) {
            return false;
        }
        if (layer.options.tileMatrixSetLimits) {
            if (c.row < layer.options.tileMatrixSetLimits[c.zoom].minTileRow ||
                c.row > layer.options.tileMatrixSetLimits[c.zoom].maxTileRow ||
                c.col < layer.options.tileMatrixSetLimits[c.zoom].minTileCol ||
                c.col > layer.options.tileMatrixSetLimits[c.zoom].maxTileCol) {
                return false;
            }
        }
    }
    return true;
}

function getColorTextures(tile, layer, targetZoom) {
    if (tile.material === null) {
        return Promise.resolve();
    }
    const promises = [];
    const bcoord = tile.getCoordsForLayer(layer);

    for (const coordWMTS of bcoord) {
        promises.push(getColorTexture(coordWMTS, layer, targetZoom));
    }

    return Promise.all(promises);
}

export default {
    preprocessDataLayer,
    executeCommand,
    tileTextureCount,
    tileInsideLimit,
};



// WEBPACK FOOTER //
// ./src/Provider/WMTSProvider.js